import html2canvas from 'html2canvas';
import { useCanvasStore } from '@/stores/canvasStore';
import { toast } from 'sonner';

export async function exportCanvasToJPG(): Promise<void> {
  const store = useCanvasStore.getState();
  const { items, currentBoardId, boards, zoom, panX, panY, setZoom, setPan } = store;

  const boardItems = items.filter(i => i.board_id === currentBoardId);
  if (boardItems.length === 0) {
    toast.error('No items on canvas to export');
    return;
  }

  // Save original view
  const origZoom = zoom;
  const origPanX = panX;
  const origPanY = panY;

  // Calculate bounding box of ALL items on this board
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const item of boardItems) {
    minX = Math.min(minX, item.position_x);
    minY = Math.min(minY, item.position_y);
    maxX = Math.max(maxX, item.position_x + item.width);
    maxY = Math.max(maxY, item.position_y + item.height);
  }

  const contentWidth = maxX - minX;
  const contentHeight = maxY - minY;
  const PADDING = 80;

  // Calculate zoom that fits ALL items in the viewport with padding
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const scaleX = (viewportWidth - PADDING * 2) / contentWidth;
  const scaleY = (viewportHeight - PADDING * 2) / contentHeight;
  let exportZoom = Math.min(scaleX, scaleY);

  // Clamp: never zoom in beyond 100%, minimum 10%
  exportZoom = Math.max(0.1, Math.min(exportZoom, 1.0));

  // For dense boards (15+ items), zoom out a bit more for overview feel
  if (boardItems.length > 15) {
    exportZoom *= 0.85;
  }

  // Center the view on all content
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;
  const exportPanX = (viewportWidth / 2) / exportZoom - centerX;
  const exportPanY = (viewportHeight / 2) / exportZoom - centerY;

  // Apply the export view
  setZoom(exportZoom);
  setPan(exportPanX, exportPanY);

  // Wait for DOM to fully re-render at new zoom/pan
  await new Promise(resolve => setTimeout(resolve, 800));

  try {
    // Find the outer canvas container (the one with overflow:hidden)
    const canvasContainer = document.querySelector('[data-canvas-export-target]') as HTMLElement;
    if (!canvasContainer) {
      toast.error('Could not find canvas element');
      setZoom(origZoom);
      setPan(origPanX, origPanY);
      return;
    }

    // Use html2canvas with specific options to avoid iframe cloning issues
    const capturedCanvas = await html2canvas(canvasContainer, {
      backgroundColor: '#F5F5F4', // stone-100 canvas background
      scale: 1, // 1x for low-res export
      useCORS: true,
      allowTaint: true,
      logging: false,
      width: viewportWidth,
      height: viewportHeight,
      x: 0,
      y: 0,
      windowWidth: viewportWidth,
      windowHeight: viewportHeight,
      foreignObjectRendering: false, // Disable foreignObject to avoid cloning issues
      removeContainer: true,
      ignoreElements: (element: Element) => {
        // Skip all UI chrome — only capture canvas items
        if (element.hasAttribute('data-export-ignore')) return true;
        if (element.classList.contains('fixed')) return true;
        // Skip SVG cluster overlays to avoid rendering issues
        if (element.tagName === 'svg' && element.closest('[data-canvas-container]')) return true;
        return false;
      },
    });

    // Convert to blob for reliable download
    capturedCanvas.toBlob((blob) => {
      if (!blob) {
        toast.error('Failed to generate image');
        setZoom(origZoom);
        setPan(origPanX, origPanY);
        return;
      }
      
      const url = URL.createObjectURL(blob);
      const boardName = boards.find(b => b.id === currentBoardId)?.name || 'canvas';
      const safeName = boardName.replace(/[^a-zA-Z0-9_-]/g, '_');
      const date = new Date().toISOString().slice(0, 10);
      
      // Create and click a download link
      const link = document.createElement('a');
      link.href = url;
      link.download = `${safeName}_${date}.jpg`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
      
      toast.success('Canvas exported as JPG');
      
      // Restore original view
      setZoom(origZoom);
      setPan(origPanX, origPanY);
    }, 'image/jpeg', 0.8);

  } catch (err) {
    console.error('Export error:', err);
    toast.error('Export failed — try again');
    // ALWAYS restore original view, even if export failed
    setZoom(origZoom);
    setPan(origPanX, origPanY);
  }
}
